/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useSshKey.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing SSH key generation and copy functionality.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useCallback } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { TelephonyServerFormData } from '../types';

type SshKeyStatus = 'none' | 'generating' | 'generated' | 'error' | 'copying' | 'copied';

export function useSshKey(formData: TelephonyServerFormData) {
  const { t } = useI18n();
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [sshKeyStatus, setSshKeyStatus] = useState<SshKeyStatus>('none');
  const [generatedSshKey, setGeneratedSshKey] = useState<string>('');
  const [isCopied, setIsCopied] = useState(false);

  const sshStatusMessage = useMemo(() => {
    switch (sshKeyStatus) {
      case 'generating':
        return t('telephonyServers.sshKey.status.generating');
      case 'generated':
        return t('telephonyServers.sshKey.status.generated');
      case 'copying':
        return t('telephonyServers.sshKey.status.installing');
      case 'copied':
        return t('telephonyServers.sshKey.status.installed');
      case 'error':
        return t('telephonyServers.sshKey.status.error');
      default:
        return t('telephonyServers.sshKey.status.default');
    }
  }, [sshKeyStatus, t]);

  const sshButtonLabel = useMemo(() => {
    if (isGeneratingKey && sshKeyStatus === 'generating') {
      return t('telephonyServers.sshKey.button.generating');
    }
    if (isGeneratingKey) {
      return t('telephonyServers.sshKey.button.installing');
    }
    if (sshKeyStatus === 'copied') {
      return t('telephonyServers.sshKey.button.installed');
    }
    if (sshKeyStatus === 'generated') {
      return t('telephonyServers.sshKey.button.generated');
    }
    if (sshKeyStatus === 'error') {
      return t('telephonyServers.sshKey.button.error');
    }
    return t('telephonyServers.sshKey.button.default');
  }, [isGeneratingKey, sshKeyStatus, t]);

  const handleGenerateSshKey = useCallback(
    async (setErrors: (errors: Record<string, string>) => void) => {
      if (!formData.host || !formData.username) {
        setErrors({
          host: !formData.host ? t('telephonyServers.validation.hostRequiredForSsh') : '',
          username: !formData.username ? t('telephonyServers.validation.usernameRequiredForSsh') : '',
        });
        return;
      }

      try {
        setIsGeneratingKey(true);
        setSshKeyStatus('generating');

        await new Promise((resolve) => setTimeout(resolve, 2000));

        const sampleSshKey = `ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAABgQC7vbqajDhA... ring2all-${formData.name}-${Date.now()}@ring2all-platform`;
        setGeneratedSshKey(sampleSshKey);
        setSshKeyStatus('generated');

        await new Promise((resolve) => setTimeout(resolve, 3000));
        setSshKeyStatus('copying');
        await new Promise((resolve) => setTimeout(resolve, 2000));
        await new Promise((resolve) => setTimeout(resolve, 1000));

        setSshKeyStatus('copied');

        try {
          await navigator.clipboard.writeText(sampleSshKey);
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 5000);
        } catch {
          /* ignore clipboard failures */
        }
      } catch {
        setSshKeyStatus('error');
      } finally {
        setIsGeneratingKey(false);
      }
    },
    [formData.host, formData.name, formData.username, t],
  );

  const handleCopySshKey = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(generatedSshKey);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      // Ignore clipboard failures
    }
  }, [generatedSshKey]);

  return {
    isGeneratingKey,
    sshKeyStatus,
    generatedSshKey,
    isCopied,
    sshStatusMessage,
    sshButtonLabel,
    handleGenerateSshKey,
    handleCopySshKey,
  };
}

